/**
 * Donation templates: their variables — the only placeholders a template has —
 * and how a donation fills them.
 *
 * A variable is a field of the template's own: a label, and the code its
 * content places as {code}. Its value is either typed into the donation form
 * (`source` 'form', which the form may require) or filled in from one of the
 * donation's own dates. A donation keeps its typed answers in `customFields`,
 * keyed by code.
 *
 * Templates once shared seven built-in placeholders. The API gave each
 * template the ones its content used as variables of its own
 * (silverkode-backend database/convert_donation_template_variables), and holds
 * every request to the same rules as these (utils/donation_custom_fields).
 */

/** Where a variable's value comes from: the donation form, or one of the donation's dates. */
export type DonationTemplateVariableSource = 'form' | 'displayStartDate' | 'displayEndDate' | 'donationDate' | 'month';
export type AutomaticVariableSource = Exclude<DonationTemplateVariableSource, 'form'>;

export interface DonationTemplateVariable {
  /** Stays the same through a rename, so donations keep their answers. */
  id: string;
  label: string;
  code: string;
  required: boolean;
  source: DonationTemplateVariableSource;
}

/** An entry in the editor's Variable menu (Rich_Text_Editor `variables`). */
export interface TemplateMenuVariable {
  value: string;
  label: string;
  required: boolean;
}

export interface CustomVariableErrors {
  label?: string;
  code?: string;
}

export const CUSTOM_VARIABLE_MAX_COUNT = 20;
export const CUSTOM_VARIABLE_LABEL_MAX_LENGTH = 60;
export const CUSTOM_VARIABLE_CODE_MAX_LENGTH = 40;
export const CUSTOM_FIELD_VALUE_MAX_LENGTH = 5000;

/** The dates a variable can fill itself in with, as the template editor offers them. */
export const AUTOMATIC_VARIABLE_SOURCES: Array<{
  value: AutomaticVariableSource;
  /** How the editor names the date. */
  label: string;
  /** The label a new variable filled with it starts with. */
  fieldLabel: string;
}> = [
  {
    value: 'displayStartDate',
    label: 'Display start date',
    fieldLabel: 'Display Start Date',
  },
  {
    value: 'displayEndDate',
    label: 'Display end date',
    fieldLabel: 'Display End Date',
  },
  {
    value: 'donationDate',
    label: 'Date added',
    fieldLabel: 'Donation Date',
  },
  {
    value: 'month',
    label: 'Hebrew month',
    fieldLabel: 'Month',
  },
];

const SOURCES: DonationTemplateVariableSource[] = ['form', ...AUTOMATIC_VARIABLE_SOURCES.map((entry) => entry.value)];

// The placeholders every template had before templates defined their own, as
// the variables the API turns them into: what a template still has when it
// comes from an API that has not given it a list (one from before variables).
const FORMER_BUILT_IN_VARIABLES: DonationTemplateVariable[] = [
  { id: 'former-title', label: 'Title', code: 'title', required: true, source: 'form' },
  { id: 'former-name', label: 'Name', code: 'name', required: true, source: 'form' },
  { id: 'former-inHonorOf', label: 'In Honor Of', code: 'inHonorOf', required: true, source: 'form' },
  { id: 'former-month', label: 'Month', code: 'month', required: false, source: 'month' },
  { id: 'former-donationDate', label: 'Donation Date', code: 'donationDate', required: false, source: 'donationDate' },
  { id: 'former-displayStartDate', label: 'Display Start Date', code: 'displayStartDate', required: false, source: 'displayStartDate' },
  { id: 'former-displayEndDate', label: 'Display End Date', code: 'displayEndDate', required: false, source: 'displayEndDate' },
];

// The donation columns the three typed built-ins were stored in. The API keeps
// them as copies of the answers to the variables with those codes.
const ANSWER_COLUMNS = ['title', 'name', 'inHonorOf'] as const;

// Names every JavaScript object answers to: an answer stored under one would
// be confused with the object's own.
const RESERVED_CODES = new Set(['constructor', 'prototype', 'tostring', 'valueof', 'hasownproperty']);

const CODE_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

/** Every {placeholder} in a template's content, as the display matches them. */
const TOKEN_PATTERN = /{(\w+)}/g;

// A placeholder someone meant to write: a name, not a stray "{1}".
const NAMED_TOKEN_PATTERN = /{([A-Za-z][A-Za-z0-9_]*)}/g;

const hasOwn = (object: object, key: string): boolean => Object.prototype.hasOwnProperty.call(object, key);

const asPlainObject = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const capitalize = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** A fresh id for a new variable. */
export const createVariableId = (): string => {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  return `cv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/** Whether a code is one no variable can take. */
export const isReservedVariableCode = (code: string): boolean => RESERVED_CODES.has(String(code || '').toLowerCase());

/** Whether a variable fills itself in from the donation's dates (rather than being typed). */
export const isAutomaticVariable = (variable: { source?: string | null } | null | undefined): boolean =>
  Boolean(variable && variable.source && variable.source !== 'form');

/** The variables the donation form asks for. */
export const typedVariables = (variables: DonationTemplateVariable[]): DonationTemplateVariable[] =>
  variables.filter((variable) => !isAutomaticVariable(variable));

/** The date an automatic variable fills itself in with. */
export const automaticSourceOf = (source: string | null | undefined) =>
  AUTOMATIC_VARIABLE_SOURCES.find((entry) => entry.value === source) || null;

/**
 * The code a label reads as: its words in camelCase — "Pledge amount" →
 * pledgeAmount, "Donor's phone #" → donorsPhone, "Café" → cafe. A label with no
 * Latin letters or digits (a Hebrew one) reads as nothing.
 */
export const toVariableCode = (label: string): string => {
  const words = String(label || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’‘`]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);

  let code = words
    .map((word, index) => (index === 0 ? word.toLowerCase() : capitalize(word.toLowerCase())))
    .join('');

  // A code starts with a letter: "2nd donor" → field2ndDonor.
  if (/^[0-9]/.test(code)) code = `field${code}`;
  return code.slice(0, CUSTOM_VARIABLE_CODE_MAX_LENGTH);
};

/**
 * The code a new variable gets from its label, kept clear of every other
 * variable's code and of the reserved ones: the label's own ("phone"), else
 * that numbered ("phone2"), else field1, field2… for a label with nothing to
 * make one from.
 */
export const suggestVariableCode = (label: string, takenCodes: string[] = []): string => {
  if (!String(label || '').trim()) return '';
  const taken = new Set(takenCodes.map((code) => String(code || '').toLowerCase()));
  const isFree = (code: string) => !taken.has(code.toLowerCase()) && !isReservedVariableCode(code);

  const base = toVariableCode(label);
  if (base && isFree(base)) return base;

  const stem = base || 'field';
  for (let number = base ? 2 : 1; ; number += 1) {
    const suffix = String(number);
    const candidate = `${stem.slice(0, CUSTOM_VARIABLE_CODE_MAX_LENGTH - suffix.length)}${suffix}`;
    if (isFree(candidate)) return candidate;
  }
};

/**
 * What is wrong with each variable, keyed by id — only the variables with a
 * problem appear. Codes are compared ignoring case: {phone} and {Phone} would
 * read as the same field to anyone writing the content.
 */
export const validateTemplateVariables = (
  variables: DonationTemplateVariable[]
): Record<string, CustomVariableErrors> => {
  const counts = new Map<string, number>();
  variables.forEach(({ code }) => {
    const key = String(code || '').trim().toLowerCase();
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });

  const errors: Record<string, CustomVariableErrors> = {};
  variables.forEach((variable) => {
    const label = String(variable.label || '').trim();
    const code = String(variable.code || '').trim();
    const entry: CustomVariableErrors = {};

    if (!label) entry.label = 'Enter a label.';
    else if (label.length > CUSTOM_VARIABLE_LABEL_MAX_LENGTH) {
      entry.label = `Keep it under ${CUSTOM_VARIABLE_LABEL_MAX_LENGTH} characters.`;
    }

    if (!code) entry.code = 'Enter a code.';
    else if (!/^[A-Za-z]/.test(code)) entry.code = 'Start the code with a letter.';
    else if (!CODE_PATTERN.test(code)) entry.code = 'Use letters and numbers only.';
    else if (code.length > CUSTOM_VARIABLE_CODE_MAX_LENGTH) {
      entry.code = `Keep it under ${CUSTOM_VARIABLE_CODE_MAX_LENGTH} characters.`;
    } else if (isReservedVariableCode(code)) entry.code = `{${code}} is reserved — choose another code.`;
    else if ((counts.get(code.toLowerCase()) || 0) > 1) entry.code = `Another field already uses {${code}}.`;

    if (entry.label || entry.code) errors[variable.id] = entry;
  });
  return errors;
};

/**
 * A template's variables as the API sends them, tidied for the forms: a
 * source on each (typed, when none is given), and `required` only on a typed
 * one. One without an id (the API always gives one) is keyed by its code, so
 * the same list read twice keeps the same keys.
 */
export const normalizeTemplateVariables = (value: unknown): DonationTemplateVariable[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item != null && typeof item === 'object')
    .map((item) => {
      const entry = item as Record<string, unknown>;
      const code = String(entry.code ?? '').trim();
      const source = SOURCES.includes(entry.source as DonationTemplateVariableSource)
        ? (entry.source as DonationTemplateVariableSource)
        : 'form';
      return {
        id: String(entry.id || '') || `code-${code}`,
        label: String(entry.label ?? '').trim(),
        code,
        required: source === 'form' && entry.required === true,
        source,
      };
    })
    .filter((variable) => variable.code);
};

/**
 * The variables a template has: its own list, or — from an API that has not
 * given the template one yet — the placeholders every template had before.
 */
export const templateVariablesOf = (template: unknown): DonationTemplateVariable[] => {
  if (template == null || typeof template !== 'object') return [];
  const list = (template as { customVariables?: unknown }).customVariables;
  return Array.isArray(list) ? normalizeTemplateVariables(list) : FORMER_BUILT_IN_VARIABLES;
};

/** The variables as a template is saved with them. */
export const templateVariablesPayload = (variables: DonationTemplateVariable[]): DonationTemplateVariable[] =>
  variables.map(({ id, label, code, required, source }) => ({
    id,
    label: String(label || '').trim(),
    code: String(code || '').trim(),
    required: source === 'form' && Boolean(required),
    source: source || 'form',
  }));

/**
 * The editor's Variable menu for a donation template: its variables whose code
 * is ready to place (`invalidIds` leaves the rest out until they are).
 */
export const donationTemplateMenuVariables = (
  variables: DonationTemplateVariable[],
  invalidIds: string[] = []
): TemplateMenuVariable[] =>
  variables
    .filter((variable) => variable.code && !invalidIds.includes(variable.id))
    .map((variable) => ({
      value: variable.code,
      label: `Insert {${variable.code}}`,
      required: !isAutomaticVariable(variable) && Boolean(variable.required),
    }));

/** Whether the content places {code}. */
export const templateHasToken = (content: string | null | undefined, code: string): boolean =>
  Boolean(code) && String(content || '').includes(`{${code}}`);

/**
 * The content with placeholders renamed — {old} → {new} for each entry — in
 * one pass, so two codes that swapped names swap places rather than merge.
 */
export const renameTemplateTokens = (content: string | null | undefined, renames: Record<string, string>): string =>
  String(content || '').replace(TOKEN_PATTERN, (match, key: string) =>
    hasOwn(renames, key) ? `{${renames[key]}}` : match
  );

/** The content without any {code} placeholder. */
export const removeTemplateToken = (content: string | null | undefined, code: string): string =>
  String(content || '').split(`{${code}}`).join('');

/**
 * Placeholders in the content that no variable fills — a typo, or a field that
 * no longer exists. The screen would show them exactly as typed.
 */
export const unknownTemplateTokens = (
  content: string | null | undefined,
  variables: DonationTemplateVariable[]
): string[] => {
  const codes = new Set(variables.map((variable) => variable.code));
  const unknown: string[] = [];
  for (const [, key] of String(content || '').matchAll(NAMED_TOKEN_PATTERN)) {
    if (!codes.has(key) && !unknown.includes(key)) unknown.push(key);
  }
  return unknown;
};

/* ------------------------------ donation forms ------------------------------ */

/** A donation's saved answers, as the form edits them. */
export const customFieldValues = (customFields: unknown): Record<string, string> => {
  const fields = asPlainObject(customFields);
  const values: Record<string, string> = {};
  Object.keys(fields).forEach((code) => {
    const value = fields[code];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      values[code] = String(value);
    }
  });
  return values;
};

/**
 * A donation's answers, keyed by code. One whose template has no list yet
 * (from an API before variables) still has them in its three old columns.
 */
export const donationAnswers = (donation: unknown): Record<string, string> => {
  const record = asPlainObject(donation);
  const answers = customFieldValues(record.customFields);
  const template = asPlainObject(record.template);
  if (Array.isArray(template.customVariables)) return answers;
  ANSWER_COLUMNS.forEach((column) => {
    const value = record[column];
    if (!hasOwn(answers, column) && typeof value === 'string' && value.trim()) answers[column] = value;
  });
  return answers;
};

/** The form's problems with a template's typed variables, keyed by code. */
export const customFieldErrors = (
  variables: DonationTemplateVariable[],
  values: Record<string, string>
): Record<string, string> => {
  const errors: Record<string, string> = {};
  typedVariables(variables).forEach((variable) => {
    const value = hasOwn(values, variable.code) ? String(values[variable.code] ?? '').trim() : '';
    if (variable.required && !value) errors[variable.code] = `${variable.label} is required`;
    else if (value.length > CUSTOM_FIELD_VALUE_MAX_LENGTH) {
      errors[variable.code] = `Keep it under ${CUSTOM_FIELD_VALUE_MAX_LENGTH} characters`;
    }
  });
  return errors;
};

/**
 * The answers a donation is saved with: one for each of the template's typed
 * variables, trimmed. Answers to the fields of a template the form switched
 * away from stay behind.
 */
export const customFieldsPayload = (
  variables: DonationTemplateVariable[],
  values: Record<string, string>
): Record<string, string> => {
  const payload: Record<string, string> = {};
  typedVariables(variables).forEach(({ code }) => {
    payload[code] = hasOwn(values, code) ? String(values[code] ?? '').trim() : '';
  });
  return payload;
};

/**
 * The answers as a donation request carries them: `customFields`, and — for
 * an API from before variables, which still requires them — the answers to
 * `title`, `name` and `inHonorOf` the old way, where the template has them.
 */
export const donationAnswersPayload = (
  variables: DonationTemplateVariable[],
  values: Record<string, string>
): { customFields: Record<string, string>; title?: string; name?: string; inHonorOf?: string } => {
  const customFields = customFieldsPayload(variables, values);
  const payload: { customFields: Record<string, string>; title?: string; name?: string; inHonorOf?: string } = {
    customFields,
  };
  ANSWER_COLUMNS.forEach((column) => {
    if (hasOwn(customFields, column)) payload[column] = customFields[column];
  });
  return payload;
};

/* ---------------------------------- lists ---------------------------------- */

/**
 * How a set of answers reads in one line: the first answer, then the next.
 * A `name` and an `inHonorOf` come first when there are ones — the order the
 * donations list always showed — and otherwise the template's own order.
 */
export const summarizeAnswers = (
  variables: DonationTemplateVariable[],
  answers: Record<string, string>
): { primary: string; secondary: string } => {
  const filled = typedVariables(variables)
    .map((variable) => ({
      code: variable.code,
      value: hasOwn(answers, variable.code) ? String(answers[variable.code] ?? '').trim() : '',
    }))
    .filter((entry) => entry.value);

  const primary = filled.find((entry) => entry.code === 'name') || filled[0];
  const rest = filled.filter((entry) => entry !== primary);
  const secondary = rest.find((entry) => entry.code === 'inHonorOf') || rest[0];
  return { primary: primary?.value || '', secondary: secondary?.value || '' };
};

/**
 * How a donation reads in a list. With nothing typed into its template's
 * fields, what it was listed by before (its old columns), if anything.
 */
export const donationSummary = (donation: unknown): { primary: string; secondary: string } => {
  const record = asPlainObject(donation);
  const summary = summarizeAnswers(templateVariablesOf(record.template), donationAnswers(record));
  if (summary.primary) return summary;
  const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return {
    primary: text(record.name) || text(record.title),
    secondary: text(record.inHonorOf),
  };
};

/* -------------------------------- rendering -------------------------------- */

export interface DonationTemplateRenderOptions {
  /** Each automatic value, as the caller writes the donation's dates out. */
  dates: Record<AutomaticVariableSource, (donation: Record<string, unknown>) => string>;
  /** Typed text, made ready for the screen (line breaks kept). */
  toHtml: (value: unknown) => string;
}

/**
 * A donation drawn through its template's content, every placeholder filled in
 * one pass — so an answer that happens to read "{name}" stays as typed. Each of
 * the template's variables takes the donation's answer (blank when it gave
 * none, never the bare {code}) or its date; anything else is left as written.
 */
export const renderDonationTemplate = (
  content: string | null | undefined,
  donation: Record<string, unknown> | null | undefined,
  { dates, toHtml }: DonationTemplateRenderOptions
): string => {
  const source = String(content || '');
  if (!source) return '';
  const record = donation || {};
  const answers = donationAnswers(record);

  const values: Record<string, string> = {};
  templateVariablesOf(record.template).forEach((variable) => {
    values[variable.code] = isAutomaticVariable(variable)
      ? dates[variable.source as AutomaticVariableSource](record)
      : toHtml(hasOwn(answers, variable.code) ? answers[variable.code] : '');
  });

  return source.replace(TOKEN_PATTERN, (match, key: string) => (hasOwn(values, key) ? values[key] : match));
};
